// Population loading uses the same consolidated report as the Metrics tab.
// No module default, invented sessions, or population-specific scoring rules.
export const isStatsEligible = (type) =>
  type === "home_care" || type === "senior_living";

export const localToday = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

export const validDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return (
    Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
};

export const fetchAllPeople = async ({
  session,
  fetchUserList,
  cancelled = () => false,
  maxPages = 200,
}) => {
  const people = new Map();
  for (let page = 1; page <= maxPages; page++) {
    if (cancelled()) throw new Error("cancelled");
    const batch = await fetchUserList({
      session,
      page,
      limit: 15,
      sortField: "createdAt",
      sortOrder: "asc",
    });
    if (!Array.isArray(batch)) throw new Error("invalid_roster");
    const before = people.size;
    batch.forEach((person) => {
      if (!person?.id) throw new Error("invalid_roster");
      people.set(String(person.id), person);
    });
    if (batch.length < 15) return [...people.values()];
    if (people.size === before) throw new Error("incomplete_roster");
  }
  throw new Error("incomplete_roster");
};

export const mapWithLimit = async (
  items,
  limit,
  worker,
  onProgress = () => {},
  cancelled = () => false,
) => {
  let next = 0;
  let done = 0;
  const results = new Array(items.length);
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (next < items.length && !cancelled()) {
        const index = next++;
        results[index] = await worker(items[index]);
        onProgress(++done, items.length);
      }
    }),
  );
  return results;
};

const gender = (value) => {
  const normalized = String(value || "").toLowerCase();
  return ["m", "male"].includes(normalized)
    ? "Male"
    : ["f", "female"].includes(normalized)
      ? "Female"
      : "";
};

export const mergePopulation = (people, reports, asOf = localToday()) => {
  const data = {
    patients_list: [],
    patient_details: {},
    measurements: {},
    history: {},
    population_roster: [],
  };
  people.forEach((person, index) => {
    const id = String(person.id);
    const report = reports[index];
    const histories = Object.values(report?.mock_data?.history || {}).flatMap(
      (metrics) => Object.values(metrics).map((history) => ({ history })),
    );
    const historyUnavailable =
      !!report?.population_history_unavailable ||
      hasAmbiguousHistory({ metrics: histories });
    const age = Number(person.age);
    data.population_roster.push({
      patient_id: id,
      age: age > 0 && Number.isFinite(age) ? age : null,
      gender: gender(person.gender),
      unavailable: !!report?.unavailable,
      history_unavailable: historyUnavailable,
    });
    const md = report?.mock_data;
    if (!md) return;
    const sourceId = Object.keys(md.measurements || {})[0];
    if (!sourceId) return;
    const measurements = md.measurements[sourceId];
    // Invalid/future session dates must not acquire the adapter's default date.
    const date = measurements.session?.record_date;
    const history = {};
    Object.entries(md.history?.[sourceId] || {}).forEach(([key, points]) => {
      const byDate = new Map();
      (points || [])
        .filter(
          (point) =>
            validDate(point.date) &&
            point.date <= asOf &&
            point.value != null &&
            Number.isFinite(Number(point.value)),
        )
        .forEach((point) => byDate.set(point.date, point));
      history[key] = [...byDate.values()].sort((a, b) =>
        a.date.localeCompare(b.date),
      );
    });
    if (historyUnavailable) {
      Object.keys(history).forEach((key) => {
        history[key] = [];
      });
      (measurements.measurements || []).forEach((measurement) => {
        if (validDate(date) && date <= asOf && measurement.value != null)
          history[measurement.metric_api] = [{ ...measurement, date }];
      });
    }
    // The current consolidated snapshot is authoritative over inline history
    // from another report on that same calendar day.
    if (validDate(date) && date <= asOf) {
      Object.keys(history).forEach((key) => {
        history[key] = history[key].filter((point) => point.date !== date);
      });
      (measurements.measurements || []).forEach((measurement) => {
        if (
          measurement.value == null ||
          !Number.isFinite(Number(measurement.value))
        )
          return;
        const points =
          history[measurement.metric_api] ||
          (history[measurement.metric_api] = []);
        points.push({ ...measurement, date });
        points.sort((a, b) => a.date.localeCompare(b.date));
      });
    }
    if (
      (!validDate(date) || date > asOf) &&
      !Object.values(history).some((points) => points.length)
    ) {
      data.population_roster[data.population_roster.length - 1].unavailable =
        true;
      return;
    }
    data.patients_list.push({ ...md.patients_list?.[0], patient_id: id });
    data.patient_details[id] = {
      ...md.patient_details?.[sourceId],
      patient_id: id,
    };
    data.history[id] = history;
    data.measurements[id] = measurements;
  });
  return { schema_version: "v5", mock_data: data };
};

// The shared report adapter collapses history to calendar days. Detect source
// collisions before that collapse so two report identities cannot form a
// synthetic historical score. Keep the canonical current snapshot, and mark
// comparisons unavailable for that person until sessions are unambiguous.
export const hasAmbiguousHistory = (summary) => {
  const days = new Map();
  let ambiguous = false;
  const visit = (value) => {
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value.history)) {
      const seen = new Map();
      value.history.forEach((point) => {
        if (!point || typeof point !== "object") return;
        const date = String(point?.date || "").slice(0, 10);
        if (!validDate(date)) return;
        const identity =
          point.report_id || point.reportId
            ? `report:${point.report_id || point.reportId}`
            : point.assessment_timestamp || point.timeStamp || point.timestamp
              ? `time:${point.assessment_timestamp || point.timeStamp || point.timestamp}`
              : "day-only";
        if (!days.has(date)) days.set(date, new Set());
        days.get(date).add(identity);
        const measurement = point.value ?? point.time ?? point.score;
        if (
          seen.has(date) &&
          (seen.get(date).identity !== identity ||
            seen.get(date).measurement !== measurement)
        )
          ambiguous = true;
        seen.set(date, { identity, measurement });
      });
    }
    Object.entries(value).forEach(([key, child]) => {
      if (key !== "history") visit(child);
    });
  };
  visit(summary);
  return (
    ambiguous || [...days.values()].some((identities) => identities.size > 1)
  );
};

export const loadPopulation = async (
  { session, fetchUserList, getReport, buildReport, cancelled = () => false },
  onProgress,
) => {
  const people = await fetchAllPeople({ session, fetchUserList, cancelled });
  const reports = await mapWithLimit(
    people,
    4,
    async (person) => {
      try {
        const response = await getReport(person.id);
        const summary =
          response?.data && typeof response.data === "object"
            ? { ...response, ...response.data }
            : response;
        if (!summary || response?.data === null) return null;
        if (summary.status === false || summary.status === 0)
          return { unavailable: true };
        // The adapter requires a real report date; it otherwise defaults to today.
        if (
          !summary.timeStamp ||
          !validDate(String(summary.timeStamp).slice(0, 10))
        )
          return { unavailable: true };
        const report = buildReport({
          summary,
          seniorId: person.id,
          moduleId: summary.moduleId,
          userInfo: {
            ...person,
            height_cm: person.height_cm ?? person.height,
            weight_kg: person.weight_kg ?? person.weight,
          },
          personName: person.name,
        });
        report.population_history_unavailable = hasAmbiguousHistory(summary);
        return report;
      } catch (error) {
        // Transport/permission failures are unknown, never "never tested".
        return { unavailable: true };
      }
    },
    onProgress,
    cancelled,
  );
  if (cancelled()) throw new Error("cancelled");
  return mergePopulation(people, reports);
};
